import { useCallback, useMemo, useState } from 'react';

import CancelIcon from '@mui/icons-material/Cancel';
import SearchIcon from '@mui/icons-material/Search';
import { FormControl, IconButton, InputAdornment, OutlinedInput } from '@mui/material';
import { OutlinedInputProps } from '@mui/material/OutlinedInput/OutlinedInput';
import { styled } from '@mui/material/styles';

import { BorderRadius } from '../styles/themeMui';

export enum SearchInputSizes {
  small = 'small',
  large = 'large',
}
export interface SearchInputProps {
  searchLabel: string;
  defaultValue?: string;
  onChange: (searchString: string) => void;
  size?: SearchInputSizes;
}

const SearchOutlinedInput = styled(OutlinedInput)<OutlinedInputProps>(({ theme }) => ({
  backgroundColor: theme.palette.secondary.main,
  borderColor: theme.palette.secondary.main,
  borderRadius: BorderRadius.Input,
  transition: 'background .1s linear',

  '& .MuiOutlinedInput-notchedOutline': {
    transition: 'border .1s linear',
    borderColor: 'transparent',
    borderWidth: 2,
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: 'transparent',
  },
  '&.Mui-focused': {
    backgroundColor: theme.palette.common.white,
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.primary.main,
  },
}));

export function SearchInput({
  searchLabel,
  defaultValue,
  onChange,
  size = SearchInputSizes.large,
}: SearchInputProps) {
  const [searchedString, setSearchedString] = useState<string>(defaultValue || '');

  const styles = useMemo(() => {
    const minHeight = size === SearchInputSizes.small ? 36 : 48;
    const iconSize = size === SearchInputSizes.small ? 32 : 36;
    const paddingLeft = size === SearchInputSizes.small ? 1 : 2;
    const fontSize = size === SearchInputSizes.small ? '0.9rem' : '1rem';

    return {
      input: {
        lineHeight: 'normal',
      },
      resetIcon: {
        height: iconSize,
        width: iconSize,
      },
      formControl: {
        '& .MuiOutlinedInput-root': { minHeight, paddingLeft, fontSize },
      },
    };
  }, [size]);

  const onChangeHandler = useCallback(
    (event) => {
      setSearchedString(event?.target?.value);
      onChange(event?.target?.value || '');
    },
    [onChange],
  );

  const handleSearchStringDelete = useCallback(() => {
    setSearchedString('');
    onChange('');
  }, [onChange]);

  const handleMouseDownDelete = useCallback((event) => {
    event.preventDefault();
  }, []);

  const computeEndAdornment = useMemo(() => {
    if (!searchedString.length) {
      return null;
    }
    return (
      <InputAdornment position="end">
        <IconButton
          onClick={handleSearchStringDelete}
          onMouseDown={handleMouseDownDelete}
          sx={styles.resetIcon}
        >
          <CancelIcon />
        </IconButton>
      </InputAdornment>
    );
  }, [handleMouseDownDelete, handleSearchStringDelete, searchedString.length, styles.resetIcon]);

  return (
    <FormControl fullWidth variant="filled" sx={styles.formControl}>
      <SearchOutlinedInput
        id="filled-adornment-search"
        value={searchedString}
        placeholder={searchLabel}
        startAdornment={
          <InputAdornment position="start">
            <SearchIcon />
          </InputAdornment>
        }
        inputProps={{
          step: '1',
          style: styles.input,
        }}
        endAdornment={computeEndAdornment}
        onChange={onChangeHandler}
      />
    </FormControl>
  );
}
